import React, { FC, useCallback, useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet";
import { Accordion, Dropdown, DropdownButton, Table, Button } from "react-bootstrap";
import Swal from "sweetalert2";

import { ProgressReportType } from "../../../DTO";
import {
    Loading,
    CustomBreadCrumb,
    WeekList,
    UserDetail,
    DayDetail,
    FoodLog,
    SleepReport,
    StressMood,
    ActivityReport,
    ActivityTargetTrace,
    SendNotification,
} from "../../../components";
import { useAppSelector } from "../../../redux-config/hooks";
import { selectUser } from "../../../redux-config/entities/user";
import {
    useAppNavigate,
    useAppParams,
    useAppQuery,
} from "../../../Router/hook";
import { AppUserApi } from "../../../api";
import { selectWeek } from "../../../redux-config/entities/week";
import "./style.scss";

type ReportType =
    | "engagement_detail"
    | "sleep_detail"
    | "activity_detail"
    | "food_detail"
    | "stress_detail"
    | "weight_detail"
    | "blood_detail";

type FoodViewMode = "weekly" | "daily";

const REPORT_OPTIONS: Array<{ key: ReportType; label: string }> = [
    { key: "engagement_detail", label: "Engagement" },
    { key: "sleep_detail", label: "Sleep" },
    { key: "activity_detail", label: "Activity" },
    { key: "food_detail", label: "Food" },
    { key: "stress_detail", label: "Stress" },
    { key: "weight_detail", label: "Weight" },
    { key: "blood_detail", label: "Blood" },
];

const REPORT_LABEL_BY_KEY: Record<ReportType, string> = REPORT_OPTIONS.reduce(
    (acc, cur) => {
        acc[cur.key] = cur.label;
        return acc;
    },
    {} as Record<ReportType, string>,
);

const isReportType = (value: string | null): value is ReportType => {
    return (
        !!value &&
        (REPORT_LABEL_BY_KEY as Record<string, string>)[value] != null
    );
};

export const UserProgressReport: FC = () => {
    const [loading, setLoading] = useState(false);
    const [progressReport, setProgressReport] = useState<ProgressReportType>();
    const [foodType, setFoodType] = useState<FoodViewMode>("weekly");
    const [currentDate, setCurrentDate] = useState("");

    const queryString = useAppQuery();
    const rawTypeQuery = queryString.get("type");
    const typeQuery: ReportType | null = isReportType(rawTypeQuery)
        ? rawTypeQuery
        : null;

    const { id } = useAppParams();
    const navigate = useAppNavigate();

    const user = useAppSelector(selectUser);
    const weekSelected = useAppSelector(selectWeek);

    const userId = useMemo(() => Number(id), [id]);

    const crumbs = useMemo(
        () => [
            { name: "", href: "" },
            { name: "Dashboard", href: "/" },
            { name: "User list", href: "/app-user" },
            {
                name: "User Detail",
                href: `/app-user/${userId}/progress/detail`,
            },
            { name: "Report " + id, href: "#" },
        ],
        [id, userId],
    );

    const selectedReportLabel = useMemo(() => {
        if (!typeQuery) return "Select report";
        return REPORT_LABEL_BY_KEY[typeQuery] ?? "Select report";
    }, [typeQuery]);

    const canFetch =
        Boolean(typeQuery) &&
        Boolean(weekSelected?.id) &&
        weekSelected.id > 0;

    const fetchProgressReport = useCallback(() => {
        if (!canFetch || !typeQuery) return;

        setLoading(true);

        AppUserApi.UserReportDetail(user, userId, weekSelected.id, typeQuery)
            .then((data) => {
                setProgressReport(data.report);
            })
            .catch((error) => {
                Swal.fire({
                    icon: "error",
                    text: error,
                });
            })
            .finally(() => {
                setLoading(false);
            });
    }, [canFetch, typeQuery, user, userId, weekSelected?.id]);

    useEffect(() => {
        if (weekSelected && weekSelected.index > 0) {
            fetchProgressReport();
        }
    }, [fetchProgressReport, weekSelected]);

    useEffect(() => {
        setFoodType("weekly");
        setCurrentDate("");
    }, [typeQuery]);

    const isFoodOrStress =
        typeQuery === "food_detail" || typeQuery === "stress_detail";
    const isDailyMode = foodType === "daily";

    const handleNavigateToType = useCallback(
        (nextType: ReportType) => {
            navigate(
                `/app-user/${userId}/progress/report?type=${nextType}&week=${weekSelected.index}`,
            );
        },
        [navigate, userId, weekSelected.index],
    );

    const weeklyPages = useMemo(() => {
        if (!progressReport) return [];
        return progressReport.pages.filter((p) => p.body.length !== 0);
    }, [progressReport]);

    return (
        <>
            <Helmet>
                <title>D2Type | Progress Report</title>
                <meta name="description" content="Basic example" />
            </Helmet>

            <div className="d-flex flex-column gap-3">
                <CustomBreadCrumb breadCrumbList={crumbs} />

                <div className="upr-shell">
                    <div className="upr-card bg-custom-white rounded-4 shadow-sm border upr-sticky-header">
                        <div className="d-flex flex-column gap-3 p-3 p-md-4">
                            <div className="d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-3">
                                <div className="d-flex align-items-center gap-2">
                                    <h4 className="mb-0">Progress Report</h4>
                                    {typeQuery ? (
                                        <span className="badge text-bg-danger">
                                            {selectedReportLabel}
                                        </span>
                                    ) : (
                                        <span className="badge text-bg-secondary">
                                            Select report
                                        </span>
                                    )}
                                </div>

                                <div className="d-flex flex-wrap align-items-center gap-2 gap-md-3">
                                    <WeekList pageId={userId} />

                                    <DropdownButton
                                        id="dropdown-basic-button"
                                        title={selectedReportLabel}
                                        variant="outline-danger"
                                        className="upr-dropdown"
                                    >
                                        {REPORT_OPTIONS.map((opt) => (
                                            <Dropdown.Item
                                                key={opt.key}
                                                active={typeQuery === opt.key}
                                                onClick={() =>
                                                    handleNavigateToType(
                                                        opt.key,
                                                    )
                                                }
                                            >
                                                {opt.label}
                                            </Dropdown.Item>
                                        ))}
                                    </DropdownButton>

                                    <div className="btn btn-outline-primary upr-dropdown upr-pill">
                                        <SendNotification
                                            fetchData={fetchProgressReport}
                                            user={user}
                                            userId={userId}
                                        />
                                    </div>
                                </div>
                            </div>

                            {loading ? (
                                <div className="p-2">
                                    <Loading />
                                </div>
                            ) : (
                                <>
                                    <div className="upr-section rounded-4 border bg-white p-3 p-md-4">
                                        <UserDetail
                                            pageId={userId}
                                            progressReport={progressReport}
                                        />
                                        {typeQuery === "activity_detail" && (
                                            <div className="mt-3">
                                                <ActivityTargetTrace
                                                    userId={userId}
                                                    compact
                                                    selectedWeekIndex={weekSelected.index}
                                                />
                                            </div>
                                        )}
                                    </div>

                                    {isFoodOrStress && (
                                        <div className="upr-section rounded-4 border bg-white p-3 p-md-4">
                                            <div className="d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-3">
                                                <div>
                                                    <div className="fw-semibold">
                                                        View mode
                                                    </div>
                                                    <div className="text-muted small">
                                                        Switch between weekly
                                                        summary and daily
                                                        details
                                                    </div>
                                                </div>

                                                <div
                                                    className="btn-group"
                                                    role="group"
                                                    aria-label="Food/Stress view mode"
                                                >
                                                    <button
                                                        type="button"
                                                        className={`btn ${
                                                            foodType ===
                                                            "weekly"
                                                                ? "btn-danger"
                                                                : "btn-outline-danger"
                                                        }`}
                                                        onClick={() =>
                                                            setFoodType(
                                                                "weekly",
                                                            )
                                                        }
                                                    >
                                                        Weekly
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className={`btn ${
                                                            foodType === "daily"
                                                                ? "btn-danger"
                                                                : "btn-outline-danger"
                                                        }`}
                                                        onClick={() =>
                                                            setFoodType("daily")
                                                        }
                                                    >
                                                        Daily
                                                    </button>
                                                </div>
                                            </div>

                                            <div className="mt-3">
                                                {isDailyMode && (
                                                    <div className="upr-section-inner">
                                                        <DayDetail
                                                            userId={userId}
                                                            currentDay={
                                                                currentDate
                                                            }
                                                            setCurrentDay={
                                                                setCurrentDate
                                                            }
                                                        />
                                                    </div>
                                                )}

                                                {typeQuery === "food_detail" &&
                                                    isDailyMode && (
                                                        <div className="upr-section-inner">
                                                            <FoodLog
                                                                userId={userId}
                                                                currentDate={
                                                                    currentDate
                                                                }
                                                            />
                                                        </div>
                                                    )}

                                                {typeQuery ===
                                                    "stress_detail" &&
                                                    isDailyMode && (
                                                        <div className="upr-section-inner">
                                                            <StressMood
                                                                userId={userId}
                                                                currentDate={
                                                                    currentDate
                                                                }
                                                            />
                                                        </div>
                                                    )}
                                            </div>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    </div>

                    {!loading && progressReport && foodType === "weekly" && (
                        <div className="upr-card bg-custom-white rounded-4 shadow-sm border p-3 p-md-4 mt-3">
                            <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
                                <div>
                                    <div className="fw-semibold">
                                        Weekly summary
                                    </div>
                                    <div className="text-muted small">
                                        Sections are grouped for easier
                                        scanning. Expand to see details.
                                    </div>
                                </div>
                                {weekSelected?.id && (
                                    <Button
                                        variant="primary"
                                        size="sm"
                                        onClick={() =>
                                            navigate(
                                                `/app-user/${userId}/food-summary/weekly/${weekSelected.id}`
                                            )
                                        }
                                        title="View detailed weekly summary"
                                    >
                                        📊 View Summary
                                    </Button>
                                )}
                            </div>

                            <Accordion
                                defaultActiveKey="0"
                                className="upr-accordion"
                            >
                                {weeklyPages.flatMap(
                                    (reportItem, reportIndex) =>
                                        reportItem.body.map(
                                            (bodyItem, bodyIndex) => {
                                                const eventKey = `${reportIndex}-${bodyIndex}`;

                                                return (
                                                    <Accordion.Item
                                                        eventKey={eventKey}
                                                        key={eventKey}
                                                        className="upr-acc-item mb-2"
                                                    >
                                                        <Accordion.Header>
                                                            <div className="d-flex align-items-center justify-content-between w-100 gap-2">
                                                                <span className="fw-semibold">
                                                                    {
                                                                        bodyItem.title
                                                                    }
                                                                </span>
                                                                <span className="badge text-bg-light border">
                                                                    {bodyItem
                                                                        .items
                                                                        ?.length ??
                                                                        0}{" "}
                                                                    items
                                                                </span>
                                                            </div>
                                                        </Accordion.Header>

                                                        <Accordion.Body>
                                                            <div className="table-responsive">
                                                                <Table
                                                                    hover
                                                                    className="align-middle mb-0"
                                                                >
                                                                    <thead>
                                                                        <tr>
                                                                            <th
                                                                                style={{
                                                                                    width: 70,
                                                                                }}
                                                                            >
                                                                                No.
                                                                            </th>
                                                                            <th>
                                                                                Label
                                                                            </th>
                                                                            <th
                                                                                style={{
                                                                                    width: "35%",
                                                                                }}
                                                                            >
                                                                                Value
                                                                            </th>
                                                                        </tr>
                                                                    </thead>
                                                                    <tbody>
                                                                        {bodyItem.items.map(
                                                                            (
                                                                                item,
                                                                                idx,
                                                                            ) => (
                                                                                <tr
                                                                                    key={
                                                                                        item.id
                                                                                    }
                                                                                >
                                                                                    <td className="text-muted">
                                                                                        {idx +
                                                                                            1}
                                                                                    </td>
                                                                                    <td className="fw-medium">
                                                                                        {
                                                                                            item.label
                                                                                        }
                                                                                    </td>
                                                                                    <td className="text-break">
                                                                                        {
                                                                                            item.value
                                                                                        }
                                                                                    </td>
                                                                                </tr>
                                                                            ),
                                                                        )}
                                                                    </tbody>
                                                                </Table>
                                                            </div>
                                                        </Accordion.Body>
                                                    </Accordion.Item>
                                                );
                                            },
                                        ),
                                )}
                            </Accordion>

                            <div className="upr-divider my-3" />

                            {typeQuery === "sleep_detail" && (
                                <div className="upr-section rounded-4 border bg-white p-3 p-md-4">
                                    <div className="fw-semibold mb-2">
                                        Sleep details
                                    </div>
                                    <SleepReport userId={userId} />
                                </div>
                            )}

                            {typeQuery === "activity_detail" && (
                                <div className="upr-section rounded-4 border bg-white p-3 p-md-4">
                                    <div className="fw-semibold mb-2">
                                        Activity details
                                    </div>
                                    <ActivityReport userId={userId} />
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </>
    );
};
